// frontend/src/features/tbs/components/RunBuilder.jsx
// Left rail: build a run. Pick a default collector, paste or upload accounts,
// check them (server-side normalization), then start.
import React, { useMemo, useRef, useState } from "react";
import { Paperclip, X, Layers, ListChecks, Play, Loader2, ChevronDown } from "lucide-react";
import { StatusDot } from "./CollectorCatalog";

const STATUS_RANK = { live: 0, degraded: 1, unavailable: 2, coming_soon: 3 };

function CollectorPicker({ collectors, value, onChange, onBrowse }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const chosen = collectors.find((c) => c.key === value);

  const matches = useMemo(() => {
    const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const nq = norm(q);
    if (!nq) return [];
    return collectors
      .filter((c) => norm(c.name).includes(nq) || norm(c.key).includes(nq) || (c.aliases || []).some((a) => norm(a).includes(nq)))
      .sort((a, b) => (STATUS_RANK[a.status] - STATUS_RANK[b.status]) || a.name.localeCompare(b.name))
      .slice(0, 12);
  }, [collectors, q]);

  if (chosen) {
    return (
      <div className="tbs-chosen">
        <StatusDot status={chosen.status} />
        <span className="nm">{chosen.name}</span>
        <span className="meta">{chosen.id_type}</span>
        <button className="tbs-x" onClick={() => { onChange(""); setQ(""); }} aria-label="Clear collector"><X size={11} /></button>
      </div>
    );
  }
  const pick = (c) => { if (!c.selectable) return; onChange(c.key); setQ(""); setOpen(false); };
  return (
    <div className="tbs-picker">
      <input className="tbs-in" value={q} placeholder="Type a county… (optional if your list names it)"
        onChange={(e) => { setQ(e.target.value); setOpen(true); setIdx(0); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(i + 1, matches.length - 1)); }
          if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
          if (e.key === "Enter" && matches[idx]) { e.preventDefault(); pick(matches[idx]); }
          if (e.key === "Escape") setOpen(false);
        }} />
      {open && q && (
        <div className="tbs-picker-list">
          {matches.length === 0 && <div className="tbs-pick"><span className="hint">No match. </span><button className="tbs-link" onMouseDown={onBrowse}>Browse all</button></div>}
          {matches.map((c, i) => (
            <button key={c.key} className={`tbs-pick ${i === idx ? "active" : ""}`} onMouseDown={() => pick(c)} disabled={!c.selectable}
              title={c.selectable ? "" : c.status_note}>
              <StatusDot status={c.status} />
              <span className="nm">{c.name}</span>
              <span className="hint">{c.selectable ? c.id_type : (c.status === "unavailable" ? "unavailable" : "coming soon")}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function RunBuilder({
  collectors, currentYear, busy,
  defaultCollector, onDefaultCollector, onBrowse,
  taxYear, onTaxYear, text, onText, file, onFile, label, onLabel,
  scrapeData, onScrapeData, scrapeSupported,
  preview, onCheck, onStart, onClearPreview, checking, starting,
}) {
  const fileRef = useRef(null);
  const [advanced, setAdvanced] = useState(false);
  const okRows = preview?.rows?.filter((r) => r.ok) || [];
  const badRows = preview?.rows?.filter((r) => !r.ok) || [];
  const canCheck = Boolean((text && text.trim()) || file);
  const years = [];
  for (let y = currentYear + 1; y >= currentYear - 6; y--) years.push(y);

  return (
    <>
      <div>
        <div className="tbs-lbl">1 · Where are the bills?</div>
        <CollectorPicker collectors={collectors} value={defaultCollector} onChange={onDefaultCollector} onBrowse={onBrowse} />
        <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
          <button className="tbs-btn sm" onClick={onBrowse}><Layers size={12} /> Browse collectors</button>
          <span className="tbs-help">Leave blank if each line names its own county.</span>
        </div>
      </div>

      <div>
        <div className="tbs-lbl">2 · Which accounts?</div>
        <textarea className="tbs-in mono" value={text} onChange={(e) => onText(e.target.value)} disabled={busy}
          placeholder={"One per line, e.g.\n00000183022000000\ndallas: 00000183022000000\nor paste two Excel columns (county, account)"} />
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          <label className="tbs-file" style={{ flex: 1 }}>
            <Paperclip size={13} />
            <span>{file ? file.name : "…or choose an .xlsx / .csv"}</span>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden disabled={busy}
              onChange={(e) => onFile(e.target.files?.[0] || null)} />
          </label>
          {file && <button className="tbs-x" style={{ width: 34, height: 34 }} onClick={() => { onFile(null); if (fileRef.current) fileRef.current.value = ""; }}><X size={12} /></button>}
        </div>
        <p className="tbs-help" style={{ marginTop: 6 }}>Spreadsheets need an account column; a county/collector column and a year column are picked up automatically.</p>
      </div>

      <div>
        <div className="tbs-lbl">3 · Which tax year?</div>
        <select className="tbs-in" value={taxYear ?? ""} onChange={(e) => onTaxYear(e.target.value === "" ? null : Number(e.target.value))} disabled={busy}>
          <option value="">Whatever the collector currently serves</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <p className="tbs-help" style={{ marginTop: 6 }}>
          Collector sites only serve their current statement. Choosing a year does not change what is fetched; it makes every statement from a different year get flagged so you can tell a fresh {currentYear} bill from last year's.
        </p>
      </div>

      <div>
        <button className="tbs-lbl" style={{ background: "none", border: "none", padding: 0, width: "100%", cursor: "pointer" }} onClick={() => setAdvanced((v) => !v)}>
          Options <span className="spacer" /> <ChevronDown size={12} style={{ transform: advanced ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
        </button>
        {advanced && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div className="tbs-field"><span>Run label</span>
              <input className="tbs-in" value={label} onChange={(e) => onLabel(e.target.value)} placeholder="e.g. Genecov Smith 2026" disabled={busy} /></div>
            <label className="tbs-toggle" title={scrapeSupported.length ? `Available for: ${scrapeSupported.join(", ")}` : "No collector with bill-data parsing in this run yet"}>
              <input type="checkbox" checked={scrapeData} onChange={(e) => onScrapeData(e.target.checked)} disabled={busy} />
              Also extract bill data to Excel (entities, rates, taxable value, amount due)
              <span className="tbs-pill info" style={{ marginLeft: "auto" }}>{scrapeSupported.length ? scrapeSupported.join(", ") : "Smith only"}</span>
            </label>
          </div>
        )}
      </div>

      <div>
        {!preview ? (
          <button className="tbs-btn block" onClick={onCheck} disabled={!canCheck || busy || checking}>
            {checking ? <Loader2 size={14} className="tbs-spin" /> : <ListChecks size={14} />} {checking ? "Checking…" : "Check accounts"}
          </button>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div className="tbs-preview-sum">
              <span className="tbs-pill ok">{okRows.length} ready</span>
              {badRows.length > 0 && <span className="tbs-pill err">{badRows.length} need attention</span>}
              <button className="tbs-link" onClick={onClearPreview} style={{ marginLeft: "auto" }}>Edit list</button>
            </div>
            <button className="tbs-btn primary block" onClick={onStart} disabled={okRows.length === 0 || starting}>
              {starting ? <Loader2 size={14} className="tbs-spin" /> : <Play size={14} />} {starting ? "Starting…" : `Start run · ${okRows.length} account${okRows.length === 1 ? "" : "s"}`}
            </button>
            {badRows.length > 0 && <p className="tbs-help">Rows that need attention are skipped. Fix them in the list and check again, or start with the ready ones.</p>}
          </div>
        )}
      </div>
    </>
  );
}
